/**
 * Synchronous fast-path *cache* of whether a user has finished (completed or
 * skipped) the first-time onboarding flow. Read synchronously during route
 * guarding so completing or skipping onboarding releases the redirect gate
 * without a round-trip (fixes FS-606: Continue / Skip bouncing back to
 * /onboarding).
 *
 * This is NOT the source of truth. The durable record lives server-side
 * (`POST /v1/onboarding/complete`, read via `GET /v1/onboarding/completed`);
 * `LoginProtectedRoute` hydrates this flag from there on login so onboarding is
 * not re-shown after a cache/localStorage clear, on a new device, or in a
 * different browser (FS-606). It is deliberately separate from
 * `/v1/onboarding/status`, which answers a different question — whether a
 * tenant's *required* questions are answered — and so can't gate the
 * first-time flow.
 *
 * Keyed by userId so a different user signing in on the same browser still sees
 * onboarding once.
 */
const STORAGE_PREFIX = 'intuigence:onboarding_completed:'

/** True if this user has previously completed or skipped onboarding. */
export function hasCompletedOnboarding(userId?: string | null): boolean {
  if (!userId) return false
  try {
    return localStorage.getItem(STORAGE_PREFIX + userId) === 'true'
  } catch {
    // Private mode / storage disabled — treat as not-yet-onboarded.
    return false
  }
}

/** Mark onboarding finished for this user so the route guard stops redirecting. */
export function markOnboardingCompleted(userId?: string | null): void {
  if (!userId) return
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, 'true')
  } catch {
    // Ignore quota / disabled-storage errors; onboarding is non-critical.
  }
}
